import { v4 as uuidv4 } from 'uuid'

import { EventData, postMsgpack, receiveMsgpack } from '@/core/db/operations/worker-utils'

export const WORKER_TIMEOUT_MS = 120000

let timeout: NodeJS.Timeout | null = null

const timeoutMap = new Map<string, NodeJS.Timeout>()

/**
 * Request specific for db worker
 */
export async function dbWorkerRequest({ type, payload }: { type: string; payload?: Record<string, any> }) {
  return new Promise<any>((resolve, reject) => {
    if (!window.dbWorker) {
      reject(new Error('DB Worker not initialized'))
      return
    }

    if (timeout) {
      clearTimeout(timeout)
    }

    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === `${type}_response`) {
        let response = event.data.payload
        if (typeof response === 'string' && response !== '') {
          response = JSON.parse(response)
        }
        resolve(response)
      }
      if (event.data.type === `${type}_err`) {
        reject(new Error(event.data.message))
      }
    }

    window.dbWorker?.addEventListener('message', handleMessage)

    window.dbWorker?.postMessage({ type, payload })

    timeout = setTimeout(() => {
      window.dbWorker?.removeEventListener('message', handleMessage)
      reject(new Error('Timeout'))
    }, WORKER_TIMEOUT_MS)
  })
}

/**
 * New Worker Request utilities for non-db worker
 */
export async function workerRequest<T = any>(worker: Worker, payload: EventData<any>): Promise<T> {
  const { type, requestId = uuidv4(), data } = payload

  return new Promise<T>((resolve, reject) => {
    if (!worker) {
      reject(new Error('worker not initialized'))
      return
    }

    const cleanup = () => {
      const existing = timeoutMap.get(requestId)
      if (existing) {
        clearTimeout(existing)
        timeoutMap.delete(requestId)
      }
      worker.removeEventListener('message', handleMessage)
    }

    const handleMessage = receiveMsgpack((eventData) => {
      if (eventData.type === `${type}_response` && eventData.requestId === requestId) {
        cleanup()
        resolve(eventData.data as T)
      }
      if (eventData.type === `${type}_err` && eventData.requestId === requestId) {
        cleanup()
        reject(new Error(eventData.error))
      }
    })

    worker.addEventListener('message', handleMessage)

    postMsgpack(worker, { type, requestId, data })

    const requestTimeout = setTimeout(() => {
      cleanup()
      reject(new Error('request timeout'))
    }, WORKER_TIMEOUT_MS)

    timeoutMap.set(requestId, requestTimeout)
  })
}
